import * as React from 'react';
import { getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import { payoutsService } from '@/services/payoutsService';
import type { Invoice } from '@/types/payment';
import { useCreatorPayoutInvoices } from '../../hooks/useCreatorPayoutInvoices';
import { InvoicesHeader } from '../payments/InvoicesHeader';
import { InvoicesList } from '../payments/InvoicesList';

export const CreatorEarningsTab: React.FC = () => {
  const { addToast } = useToast();
  const { invoices, isLoading, isError, periodLabel, setPeriod, refetch } =
    useCreatorPayoutInvoices();

  const download = React.useCallback(
    async (invoice: Invoice) => {
      if (!invoice.payoutId || !invoice.invoiceType) return;
      try {
        const result = await payoutsService.getInvoiceDownloadUrl(
          invoice.payoutId,
          invoice.invoiceType
        );
        window.open(result.url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        addToast({
          title: 'Payout invoice unavailable',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      }
    },
    [addToast]
  );

  const downloadAll = React.useCallback(async () => {
    await Promise.allSettled(invoices.map(download));
  }, [download, invoices]);

  return (
    <section aria-labelledby='creator-earnings-title' className='animate-slide-in w-full pt-2'>
      <div className='mb-7'>
        <h2 id='creator-earnings-title' className='font-sans text-24 font-bold text-brand-800'>
          Creator earnings
        </h2>
        <p className='mt-1 font-sans text-14 text-gray-650'>
          Download the revenue-share and platform-fee documents generated for your payouts.
        </p>
      </div>
      <InvoicesHeader
        periodLabel={periodLabel}
        onPeriodChange={(year, month) => setPeriod({ year, month })}
        onDownloadAll={() => void downloadAll()}
        canDownload={invoices.length > 0}
        hideTitle
      />
      {isLoading ? (
        <div className='py-8 text-center font-sans text-gray-500'>Loading payout invoices...</div>
      ) : isError ? (
        <div className='rounded-16 border border-red-150 bg-white p-6' role='alert'>
          <p className='font-sans text-16 font-bold text-brand-800'>Payout invoices did not load</p>
          <button
            type='button'
            onClick={refetch}
            className='mt-3 font-sans text-14 font-bold text-brand-800 underline'
          >
            Retry loading
          </button>
        </div>
      ) : invoices.length > 0 ? (
        <InvoicesList invoices={invoices} onDownload={(invoice) => void download(invoice)} />
      ) : (
        <div className='py-8 text-center font-sans text-gray-500'>
          No payout invoices are available for this period.
        </div>
      )}
    </section>
  );
};
